import RentalStatus from "../enums/RentalStatus";

/**
 * Vérifie si une date est dans le mois sélectionné
 */
export const isInSelectedMonth = (date, selectedMonth, selectedYear) => {
  if (!date) return false;
  const d = new Date(date);
  return d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear;
};

/**
 * Vérifie si une date est avant le mois sélectionné
 */
export const isBeforeSelectedMonth = (date, selectedMonth, selectedYear) => {
  if (!date) return false;
  const d = new Date(date);
  const selectedDate = new Date(selectedYear, selectedMonth - 1, 1);
  return d < selectedDate;
};

/**
 * Vérifie si une date est après le mois sélectionné
 */
export const isAfterSelectedMonth = (date, selectedMonth, selectedYear) => {
  if (!date) return false;
  const d = new Date(date);
  const selectedDate = new Date(selectedYear, selectedMonth, 0); // Last day of month
  return d > selectedDate;
};

/**
 * Filtre les locations par catégorie (démarrages, en cours, fins, à venir)
 */
export const filterRentalsByCategory = (
  rentals,
  selectedMonth,
  selectedYear,
) => {
  const starts = [];
  const ongoing = [];
  const ends = [];
  const upcoming = [];

  rentals.forEach((rental) => {
    const startsThisMonth = isInSelectedMonth(
      rental.start_date,
      selectedMonth,
      selectedYear,
    );
    const endsThisMonth =
      rental.end_date &&
      isInSelectedMonth(rental.end_date, selectedMonth, selectedYear);
    const startedBefore = isBeforeSelectedMonth(
      rental.start_date,
      selectedMonth,
      selectedYear,
    );
    const startsAfter = isAfterSelectedMonth(
      rental.start_date,
      selectedMonth,
      selectedYear,
    );
    const endsAfter =
      !rental.end_date ||
      isAfterSelectedMonth(rental.end_date, selectedMonth, selectedYear);

    // Priority logic to avoid duplicates:
    // 1. If starts AND ends in the same month -> put in "starts" only
    // 2. If starts this month (but doesn't end) -> put in "starts"
    // 3. If ends this month (but didn't start) -> put in "ends"
    // 4. If ongoing (started before, ends after or no end) -> put in "ongoing"
    // 5. If starts after this month -> put in "upcoming"

    if (startsThisMonth) {
      // Include rentals that start this month (regardless if they end this month too)
      starts.push(rental);
    } else if (endsThisMonth) {
      // Include rentals that end this month (but didn't start this month)
      ends.push(rental);
    } else if (startedBefore && endsAfter) {
      // Ongoing rentals (started before and either no end date or ends after)
      ongoing.push(rental);
    } else if (startsAfter) {
      // Upcoming rentals (start after the selected month)
      upcoming.push(rental);
    }
  });

  return { starts, ongoing, ends, upcoming };
};

/**
 * Calcule le prix mensuel d'une location
 */
export const calculateMonthlyPrice = (rental, selectedMonth, selectedYear) => {
  // Exclure les locations à traiter (EN_ATTENTE), en préparation (EN_PREPARATION) et à livrer (A_LIVRER) du calcul
  if (rental.status === RentalStatus.EN_ATTENTE || rental.status === RentalStatus.EN_PREPARATION || rental.status === RentalStatus.A_LIVRER) {
    return 0;
  }

  // Déterminer le prix journalier : d'abord vérifier daily_price, sinon prix du produit
  let dailyRate = 0;
  if (rental.daily_price && !isNaN(parseFloat(rental.daily_price))) {
    dailyRate = parseFloat(rental.daily_price);
  } else if (
    rental.product &&
    rental.product.prix_u_ht_emera &&
    !isNaN(parseFloat(rental.product.prix_u_ht_emera))
  ) {
    dailyRate = parseFloat(rental.product.prix_u_ht_emera);
  } else {
    return 0; // Pas de prix disponible
  }

  const startDate = new Date(rental.start_date);
  const endDate = rental.end_date ? new Date(rental.end_date) : null;

  // Work with date strings to avoid timezone issues
  const monthStartStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const monthEndStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;

  // Convert rental dates to YYYY-MM-DD format
  const rentalStartStr = startDate.toISOString().split("T")[0];
  const rentalEndStr = endDate
    ? endDate.toISOString().split("T")[0]
    : monthEndStr;

  // Determine the actual rental period within this month
  const actualStartStr =
    rentalStartStr > monthStartStr ? rentalStartStr : monthStartStr;
  const actualEndStr = rentalEndStr < monthEndStr ? rentalEndStr : monthEndStr;

  // If no overlap, return 0
  if (actualStartStr > actualEndStr) return 0;

  // Calculate days between dates (inclusive)
  const actualStart = new Date(actualStartStr + "T00:00:00");
  const actualEnd = new Date(actualEndStr + "T00:00:00");
  const daysInRental =
    Math.floor((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;

  // Calculate price: daily rate × actual days in the month
  return dailyRate * daysInRental;
};

/**
 * Calcule le total d'une section de locations
 */
export const calculateSectionTotal = (rentals, selectedMonth, selectedYear) => {
  return rentals.reduce(
    (total, rental) =>
      total + calculateMonthlyPrice(rental, selectedMonth, selectedYear),
    0,
  );
};

/**
 * Formate un prix pour l'affichage
 */
export const formatPrice = (price) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(price);
};

/**
 * Formate un statut pour l'affichage
 */
export const formatStatus = (status) => {
  switch (status) {
    case RentalStatus.EN_ATTENTE:
      return { text: "À traiter", color: "info" };
    case RentalStatus.EN_PREPARATION:
      return { text: "En préparation", color: "info" };
    case RentalStatus.A_LIVRER:
      return { text: "À livrer", color: "primary" };
    case RentalStatus.ACTIF:
      return { text: "Actif", color: "success" };
    case RentalStatus.TERMINE:
      return { text: "Terminé", color: "secondary" };
    case RentalStatus.DATE_DEPASSEE:
      return { text: "Date dépassée", color: "danger" };
    case RentalStatus.ATTENTE_FIN:
      return { text: "Fin demandée", color: "warning" };
    case RentalStatus.A_RECUPERER:
      return { text: "À récupérer", color: "warning" };
    default:
      return { text: "En attente", color: "info" };
  }
};
